/**
 * A pragmatic Markdown (CommonMark-ish) parser, for wiki articles and
 * publication sections that turn out to be plain Markdown rather than
 * NIP-54's mandated Djot or NKBIP-01's AsciiDoc — which happens often in
 * practice, since Nostr is permissionless and most people default to
 * Markdown out of habit. See `@/lib/contentFormat` for how Wikistr decides
 * which parser to use.
 *
 * This mirrors the structure and security posture of `@/lib/asciidoc.ts`
 * and `@djot/djot`: block-level line scanning, then inline tokenizing,
 * producing a plain-data AST that `MarkdownContent` renders to React
 * elements with zero `dangerouslySetInnerHTML`. Raw inline/block HTML
 * (valid in CommonMark) is intentionally NOT implemented — there's no
 * HTML string produced for it to leak.
 *
 * Covers: ATX (`#`) and setext (`Text\n===`) headings, paragraphs,
 * bullet/ordered lists (with nesting via indentation), blockquotes,
 * fenced and indented code blocks, thematic breaks, GFM-style pipe
 * tables, and inline formatting (`**strong**`/`__strong__`,
 * `*emphasis*`/`_emphasis_`, `` `code` ``, `[text](url)`, `![alt](url)`,
 * autolinks, hard line breaks). Also understands double-bracket
 * wikilinks (`[[Target]]` / `[[Target|Display]]`) for parity with the
 * Djot renderer's wikilink convenience syntax.
 */

export type MdInline =
  | { tag: 'text'; text: string }
  | { tag: 'strong'; children: MdInline[] }
  | { tag: 'emphasis'; children: MdInline[] }
  | { tag: 'code'; text: string }
  | { tag: 'link'; href: string; children: MdInline[] }
  | { tag: 'image'; src: string; alt: string }
  | { tag: 'wikilink'; target: string; display?: string }
  | { tag: 'break' };

export interface MdListItem {
  children: MdInline[];
  sublist?: MdBlock;
}

export type MdBlock =
  | { tag: 'heading'; level: number; id: string; children: MdInline[] }
  | { tag: 'paragraph'; children: MdInline[] }
  | { tag: 'blockquote'; children: MdBlock[] }
  | { tag: 'code'; lang?: string; text: string }
  | { tag: 'thematic_break' }
  | { tag: 'bullet_list'; items: MdListItem[] }
  | { tag: 'ordered_list'; start: number; items: MdListItem[] }
  | { tag: 'table'; rows: MdInline[][][]; alignments: TableAlign[] };

export type TableAlign = 'left' | 'right' | 'center' | undefined;

export interface MdDoc {
  children: MdBlock[];
}

function slugifyHeadingId(text: string, used: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s_-]/gu, '')
      .trim()
      .replace(/\s+/g, '-') || 'section';
  let id = base;
  let i = 1;
  while (used.has(id)) {
    id = `${base}-${i}`;
    i += 1;
  }
  used.add(id);
  return id;
}

/** Parses Markdown into an AST. Never throws — falls back to an empty document on parse errors. */
export function parseMarkdown(source: string): MdDoc {
  try {
    const lines = source.replace(/\r\n/g, '\n').split('\n');
    const usedIds = new Set<string>();
    const { blocks } = parseBlocks(lines, 0, lines.length, usedIds);
    return { children: blocks };
  } catch {
    return { children: [] };
  }
}

function isThematicBreak(line: string): boolean {
  return /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line);
}

function matchAtxHeading(line: string): { level: number; text: string } | undefined {
  const m = line.match(/^ {0,3}(#{1,6})(?:\s+(.*?))?\s*$/);
  if (!m) return undefined;
  // Strip optional closing sequence of `#` characters.
  const text = (m[2] ?? '').replace(/\s+#+\s*$/, '');
  return { level: m[1].length, text };
}

function isSetextUnderline(line: string): 1 | 2 | 0 {
  if (/^ {0,3}=+\s*$/.test(line)) return 1;
  if (/^ {0,3}-{2,}\s*$/.test(line)) return 2;
  return 0;
}

function matchListMarker(line: string): { ordered: boolean; start?: number; markerLen: number; contentIndent: number } | undefined {
  const bullet = line.match(/^( {0,3})([-*+])(\s+)(\S.*)?$/);
  if (bullet) {
    const [, indent, marker, spacer] = bullet;
    return { ordered: false, markerLen: 1, contentIndent: indent.length + marker.length + spacer.length };
  }
  const ordered = line.match(/^( {0,3})(\d{1,9})([.)])(\s+)(\S.*)?$/);
  if (ordered) {
    const [, indent, num, , spacer] = ordered;
    return {
      ordered: true,
      start: Number(num),
      markerLen: num.length + 1,
      contentIndent: indent.length + num.length + 1 + spacer.length,
    };
  }
  return undefined;
}

function isFenceStart(line: string): { char: string; len: number; lang?: string } | undefined {
  const m = line.match(/^ {0,3}(`{3,}|~{3,})\s*(\S*)?/);
  if (!m) return undefined;
  return { char: m[1][0], len: m[1].length, lang: m[2] || undefined };
}

function isTableSeparatorRow(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('-')) return false;
  const cells = splitTableRow(trimmed);
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-+:?$/.test(c.trim()));
}

function splitTableRow(line: string): string[] {
  let stripped = line.trim();
  if (stripped.startsWith('|')) stripped = stripped.slice(1);
  if (stripped.endsWith('|') && !stripped.endsWith('\\|')) stripped = stripped.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < stripped.length; i++) {
    const ch = stripped[i];
    if (ch === '\\' && stripped[i + 1] === '|') {
      current += '|';
      i += 1;
      continue;
    }
    if (ch === '|') {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

function parseTableAlignments(sepLine: string): TableAlign[] {
  return splitTableRow(sepLine).map((cell) => {
    const c = cell.trim();
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return undefined;
  });
}

interface ParseBlocksResult {
  blocks: MdBlock[];
}

function parseBlocks(lines: string[], start: number, end: number, usedIds: Set<string>): ParseBlocksResult {
  const blocks: MdBlock[] = [];
  let i = start;

  while (i < end) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    if (isThematicBreak(line)) {
      blocks.push({ tag: 'thematic_break' });
      i += 1;
      continue;
    }

    const atx = matchAtxHeading(line);
    if (atx) {
      const id = slugifyHeadingId(atx.text, usedIds);
      blocks.push({ tag: 'heading', level: atx.level, id, children: parseInline(atx.text) });
      i += 1;
      continue;
    }

    // Setext heading: current line is plain text, next line is a run of
    // `=` (level 1) or `-` (level 2).
    if (i + 1 < end) {
      const underline = isSetextUnderline(lines[i + 1]);
      if (underline && line.trim() !== '' && !matchListMarker(line) && !isFenceStart(line) && !line.startsWith('>')) {
        const text = line.trim();
        const id = slugifyHeadingId(text, usedIds);
        blocks.push({ tag: 'heading', level: underline, id, children: parseInline(text) });
        i += 2;
        continue;
      }
    }

    const fence = isFenceStart(line);
    if (fence) {
      let j = i + 1;
      const codeLines: string[] = [];
      while (j < end) {
        const closeMatch = lines[j].match(new RegExp(`^ {0,3}(${fence.char === '`' ? '`' : '~'}{${fence.len},})\\s*$`));
        if (closeMatch) break;
        codeLines.push(lines[j]);
        j += 1;
      }
      blocks.push({ tag: 'code', lang: fence.lang, text: codeLines.join('\n') });
      i = j + 1;
      continue;
    }

    // Indented code block (4+ spaces), only when not inside a list context.
    if (/^ {4}\S/.test(line)) {
      let j = i;
      const codeLines: string[] = [];
      while (j < end && (/^ {4}/.test(lines[j]) || lines[j].trim() === '')) {
        codeLines.push(lines[j].replace(/^ {4}/, ''));
        j += 1;
      }
      while (codeLines.length > 0 && codeLines[codeLines.length - 1].trim() === '') codeLines.pop();
      blocks.push({ tag: 'code', text: codeLines.join('\n') });
      i = j;
      continue;
    }

    if (/^ {0,3}>/.test(line)) {
      let j = i;
      const quoteLines: string[] = [];
      while (j < end && (/^ {0,3}>/.test(lines[j]) || (lines[j].trim() !== '' && quoteLines.length > 0 && !isBlockStart(lines[j])))) {
        quoteLines.push(lines[j].replace(/^ {0,3}>\s?/, ''));
        j += 1;
      }
      const inner = parseBlocks(quoteLines, 0, quoteLines.length, usedIds);
      blocks.push({ tag: 'blockquote', children: inner.blocks });
      i = j;
      continue;
    }

    // GFM pipe table: a header row immediately followed by a separator row.
    if (line.includes('|') && i + 1 < end && isTableSeparatorRow(lines[i + 1])) {
      const alignments = parseTableAlignments(lines[i + 1]);
      const rows: MdInline[][][] = [splitTableRow(line).map((c) => parseInline(c.trim()))];
      let j = i + 2;
      while (j < end && lines[j].trim() !== '' && lines[j].includes('|')) {
        rows.push(splitTableRow(lines[j]).map((c) => parseInline(c.trim())));
        j += 1;
      }
      blocks.push({ tag: 'table', rows, alignments });
      i = j;
      continue;
    }

    const listMatch = matchListMarker(line);
    if (listMatch) {
      const { block, next } = parseList(lines, i, end, listMatch.ordered, listMatch.markerLen, usedIds);
      blocks.push(block);
      i = next;
      continue;
    }

    // Fallback: paragraph — a run of contiguous non-blank lines that don't
    // start a different block type.
    const paraLines = [line];
    let j = i + 1;
    while (j < end) {
      const next = lines[j];
      if (next.trim() === '') break;
      if (isBlockStart(next)) break;
      // Lazy setext continuation check: if the *next* line is a setext
      // underline, stop here so the outer loop reprocesses it as a heading.
      if (j + 1 <= end && isSetextUnderline(next)) break;
      paraLines.push(next);
      j += 1;
    }
    const joined = paraLines
      .map((l) => (l.endsWith('  ') || l.endsWith('\\') ? `${l.replace(/(?:  |\\)$/, '')}\u0000` : l))
      .join('\n');
    blocks.push({ tag: 'paragraph', children: parseInline(joined) });
    i = j;
  }

  return { blocks };
}

function isBlockStart(line: string): boolean {
  if (isThematicBreak(line)) return true;
  if (matchAtxHeading(line)) return true;
  if (isFenceStart(line)) return true;
  if (/^ {0,3}>/.test(line)) return true;
  if (matchListMarker(line)) return true;
  return false;
}

function parseList(
  lines: string[],
  start: number,
  end: number,
  ordered: boolean,
  markerLen: number,
  usedIds: Set<string>,
): { block: MdBlock; next: number } {
  const items: MdListItem[] = [];
  let i = start;
  let startNum = 1;
  let first = true;

  while (i < end) {
    const marker = matchListMarker(lines[i]);
    if (!marker || marker.ordered !== ordered) break;
    if (first) {
      startNum = marker.start ?? 1;
      first = false;
    }

    const contentIndent = marker.contentIndent;
    const firstLineContent = lines[i].slice(contentIndent);
    const itemLines = [firstLineContent];
    let j = i + 1;
    let sublistStart = -1;

    while (j < end) {
      const next = lines[j];
      if (next.trim() === '') {
        // A blank line ends the item unless followed by an indented
        // continuation (loose list item with multiple paragraphs) — kept
        // simple here: stop at the blank line.
        break;
      }
      const indentMatch = next.match(/^ */);
      const indentLen = indentMatch ? indentMatch[0].length : 0;
      if (indentLen >= contentIndent) {
        const dedented = next.slice(contentIndent);
        const nestedMarker = matchListMarker(dedented);
        if (nestedMarker) {
          sublistStart = j;
          break;
        }
        itemLines.push(dedented);
        j += 1;
        continue;
      }
      const siblingMarker = matchListMarker(next);
      if (siblingMarker) break;
      if (isBlockStart(next)) break;
      // Lazy continuation (unindented text right after item content).
      itemLines.push(next);
      j += 1;
    }

    let sublist: MdBlock | undefined;
    let nextIndex = j;
    if (sublistStart !== -1) {
      const subLines = lines.slice(sublistStart, end).map((l) => l.slice(Math.min(contentIndent, l.length)));
      const subMarker = matchListMarker(subLines[0]);
      if (subMarker) {
        const parsed = parseList(subLines, 0, subLines.length, subMarker.ordered, subMarker.markerLen, usedIds);
        sublist = parsed.block;
        nextIndex = sublistStart + parsed.next;
      }
    }

    items.push({ children: parseInline(itemLines.join('\n')), sublist });
    i = nextIndex;

    if (i < end && lines[i]?.trim() === '') {
      const afterBlank = i + 1;
      if (afterBlank < end && matchListMarker(lines[afterBlank])?.ordered === ordered) {
        i = afterBlank;
      }
    }
  }

  const block: MdBlock = ordered
    ? { tag: 'ordered_list', start: startNum, items }
    : { tag: 'bullet_list', items };
  return { block, next: i };
}

// ---------------------------------------------------------------------------
// Inline parsing
// ---------------------------------------------------------------------------

export function parseInline(text: string): MdInline[] {
  return parseInlineSpan(text);
}

function parseInlineSpan(text: string): MdInline[] {
  const out: MdInline[] = [];
  let buffer = '';
  let i = 0;

  const flushText = () => {
    if (buffer) {
      out.push({ tag: 'text', text: buffer });
      buffer = '';
    }
  };

  while (i < text.length) {
    const ch = text[i];

    if (ch === '\u0000') {
      flushText();
      out.push({ tag: 'break' });
      i += 1;
      continue;
    }

    // Wikilink: [[Target]] or [[Target|Display]].
    if (ch === '[' && text[i + 1] === '[') {
      const closeIdx = text.indexOf(']]', i + 2);
      if (closeIdx !== -1) {
        const inner = text.slice(i + 2, closeIdx);
        const pipeIdx = inner.indexOf('|');
        const target = pipeIdx === -1 ? inner : inner.slice(0, pipeIdx);
        const display = pipeIdx === -1 ? undefined : inner.slice(pipeIdx + 1).trim();
        flushText();
        out.push({ tag: 'wikilink', target: target.trim(), display: display || undefined });
        i = closeIdx + 2;
        continue;
      }
    }

    // Image: ![alt](url)
    if (ch === '!' && text[i + 1] === '[') {
      const m = matchLinkOrImage(text, i + 1);
      if (m) {
        flushText();
        out.push({ tag: 'image', src: m.href, alt: adocLikeAltText(m.children) });
        i = m.end;
        continue;
      }
    }

    // Link: [text](url)
    if (ch === '[') {
      const m = matchLinkOrImage(text, i);
      if (m) {
        flushText();
        out.push({ tag: 'link', href: m.href, children: m.children });
        i = m.end;
        continue;
      }
    }

    // Autolink: <https://example.com>
    if (ch === '<') {
      const m = text.slice(i).match(/^<(https?:\/\/[^\s<>]+)>/);
      if (m) {
        flushText();
        out.push({ tag: 'link', href: m[1], children: [{ tag: 'text', text: m[1] }] });
        i += m[0].length;
        continue;
      }
    }

    // Bare autolink (not wrapped in <>).
    if (/^https?:\/\//.test(text.slice(i))) {
      const m = text.slice(i).match(/^https?:\/\/[^\s<>[\]]+/);
      if (m) {
        flushText();
        const href = m[0].replace(/[.,;:!?)]+$/, '');
        out.push({ tag: 'link', href, children: [{ tag: 'text', text: href }] });
        i += href.length;
        continue;
      }
    }

    // Inline code: `code`
    if (ch === '`') {
      const closeIdx = text.indexOf('`', i + 1);
      if (closeIdx !== -1) {
        flushText();
        out.push({ tag: 'code', text: text.slice(i + 1, closeIdx) });
        i = closeIdx + 1;
        continue;
      }
    }

    // Strong: **text** or __text__
    if ((ch === '*' && text[i + 1] === '*') || (ch === '_' && text[i + 1] === '_')) {
      const delim = ch + ch;
      const closeIdx = text.indexOf(delim, i + 2);
      if (closeIdx !== -1 && closeIdx > i + 2) {
        flushText();
        out.push({ tag: 'strong', children: parseInlineSpan(text.slice(i + 2, closeIdx)) });
        i = closeIdx + 2;
        continue;
      }
    }

    // Emphasis: *text* or _text_
    if (ch === '*' || ch === '_') {
      if (text[i + 1] !== ' ' && text[i + 1] !== undefined) {
        const closeIdx = findConstrainedCloser(text, i + 1, ch);
        if (closeIdx !== -1) {
          flushText();
          out.push({ tag: 'emphasis', children: parseInlineSpan(text.slice(i + 1, closeIdx)) });
          i = closeIdx + 1;
          continue;
        }
      }
    }

    buffer += ch;
    i += 1;
  }

  flushText();
  return out;
}

function findConstrainedCloser(text: string, from: number, char: string): number {
  for (let j = from; j < text.length; j++) {
    if (text[j] === char && text[j - 1] !== ' ' && j > from) {
      // Avoid matching the second `*` of a `**` pair as a lone closer.
      if (char === '*' && text[j + 1] === '*') continue;
      if (char === '_' && text[j + 1] === '_') continue;
      return j;
    }
  }
  return -1;
}

function matchLinkOrImage(text: string, bracketStart: number): { href: string; children: MdInline[]; end: number } | undefined {
  if (text[bracketStart] !== '[') return undefined;
  let depth = 1;
  let j = bracketStart + 1;
  while (j < text.length && depth > 0) {
    if (text[j] === '[') depth += 1;
    else if (text[j] === ']') depth -= 1;
    if (depth === 0) break;
    j += 1;
  }
  if (depth !== 0) return undefined;
  const labelEnd = j; // index of closing `]`
  if (text[labelEnd + 1] !== '(') return undefined;
  const parenStart = labelEnd + 2;
  const parenEnd = text.indexOf(')', parenStart);
  if (parenEnd === -1) return undefined;

  const label = text.slice(bracketStart + 1, labelEnd);
  const hrefRaw = text.slice(parenStart, parenEnd).trim();
  // Strip an optional `"title"` suffix: `url "title"`.
  const hrefMatch = hrefRaw.match(/^(\S+)(?:\s+"[^"]*")?$/);
  const href = (hrefMatch ? hrefMatch[1] : hrefRaw).replace(/^<|>$/g, '');

  return { href, children: parseInlineSpan(label), end: parenEnd + 1 };
}

function adocLikeAltText(nodes: MdInline[]): string {
  return mdStringContent(nodes);
}

/** Plain-text content of a run of inline nodes (for alt text, heading ids, etc.). */
export function mdStringContent(nodes: MdInline[]): string {
  let out = '';
  for (const node of nodes) {
    switch (node.tag) {
      case 'text':
        out += node.text;
        break;
      case 'break':
        out += '\n';
        break;
      case 'code':
        out += node.text;
        break;
      case 'image':
        out += node.alt;
        break;
      case 'wikilink':
        out += node.display || node.target;
        break;
      default:
        out += mdStringContent(node.children);
    }
  }
  return out;
}
